/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import type { ReactNode } from "react";

import type { Dayjs } from "dayjs";

import type { IconName } from "@similarweb/icons";

import type { ICampaignLocationAndLanguage } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type {
    ICampaignsBaseScrapingConfiguration,
    ICampaignsScrapingConfiguration,
} from "app/components/campaigns/shared/CampaignsConst";
import type { IKeywords } from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/adding-keywords-step/keywords-table/BrandMonitoringCampaignTableConsts";
import type { AssetUser } from "app/pages/digital-marketing/keywords-campaigns/edit-campaign/EditCampaignSettings.consts";
import type { TagsOfKeywordCellSingleItem } from "app/pages/digital-marketing/keywords-campaigns/shared/components/TagsOfKeywordCell/types";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import type { WebsitePageSearchTypeNumbers } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import type { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import type { ValueOf } from "pro-features/types";

export type GraphAxis = {
    x: number;
    y: number;
    isPartial: boolean;
};

export interface ICampaignsConsumptionMetrics {
    usedKeywords: number;
    totalKeywords: number;
    usedCampaigns: number;
    totalCampaigns: number;
    hasMutualAssetsError: boolean;
}

export interface IMyUserConsumptionMetrics {
    usedKeywords: number;
    usedCampaigns: number;
}

export interface ITargetUrlIconProps {
    buttonIcon: IconName;
    iconColor: string;
}

export interface IKeywordAndTags extends IKeywords {
    tags: TagsOfKeywordCellSingleItem[];
    keywordToTargetUrls?: string[];
    targetUrlIcon?: ITargetUrlIconProps;
    isImportFromModal?: boolean;
    topUrl?: string;
    targetUrls?: string[];
    createdAt?: number;
}

export enum EKeywordsCampaignsWizardPages {
    CREATE_PROJECT_STEP = 0,
    SEARCH_ENGINES_STEP = 1,
    KEYWORDS_STEP = 2,
    REVIEW_STEP = 3,
}

export enum ECampaignsLoadingState {
    LOADING,
    ERROR,
    DONE,
}

export interface IKeywordsCampaignsWizardCompetitor {
    id: string;
    name: string;
    icon: string;
    displayText?: string;
    type?: EAssetType;
    selected?: boolean;
}

export interface IKeywordsCampaignsScrapingKeywords {
    [key: string]: string[];
}

export interface IRTKeywordsTagsScraping {
    [key: string]: [tags: string[], keywordToTargetUrls: string[], se: string[]];
}

export interface IRankTrackerCompetitor {
    site: string;
    asset?: string;
    color: string;
}

export interface IRTUpdateCampaignRemovedKeywords {
    scrapingGuid: string;
    removedKeywords?: string[];
}

export interface IRTUpdateCampaignKeywords extends IRTUpdateCampaignRemovedKeywords {
    addedKeywords?: string[];
}

export interface IRTRemoveKeywordsOrUpdateTagsOnly {
    updatedTags?: IKeywordsCampaignsScrapingKeywords;
    keywordChanges?: IRTUpdateCampaignRemovedKeywords[];
    updatedKeywordToTargetUrls?: IKeywordsCampaignsScrapingKeywords;
}

export interface IRTUpdateCampaignTableData {
    keywordChanges?: IRTUpdateCampaignKeywords[];
    updatedTags?: IKeywordsCampaignsScrapingKeywords;
    updatedKeywordToTargetUrls?: Record<string, string[]>;
}

export interface IKeywordsCampaignsUpdateCampaignData extends IRTUpdateCampaignTableData {
    competitors?: string[];
    name?: string;
    site?: string;
    removedScrapingConfigurationGuids?: string[];
    addedScrapingConfigurations?: ICampaignsBaseScrapingConfiguration[];
    entities?: {
        [entity: string]: string[];
    };
    assetRolesSettings: Record<"userAssetRoles", AssetUser[]>;
    isSubscribed?: boolean;
}

interface IRTBaseCampaignParams {
    name: string;
    site: string;
    competitors: string[] | IRankTrackerCompetitor[];
    entities: {
        [entity: string]: string[];
    };
    tags: IKeywordsCampaignsScrapingKeywords;
    keywordToTargetUrls: IKeywordsCampaignsScrapingKeywords;
    trackingScope?: ValueOf<typeof WebsitePageSearchTypeNumbers>;
}

export interface IKeywordsCampaignData extends IRTBaseCampaignParams {
    id: string;
    asset: string;
    color: string;
    createdTime: Dayjs;
    lastMigrationDate: Dayjs;
    /** First day with scraping data; `null` or omitted while scrape is pending. */
    firstScrapeTime?: string | null;
    /** Latest scraped day for this campaign from API; `null` while scrape is in progress. */
    lastScrapeDate: string | null;
    isShared: boolean;
    createdBy?: string;
    keywordCount: number;
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    assetRolesSettings?: Record<"userAssetRoles", AssetUser[]>;
    isDemo?: boolean;
    isEditable?: boolean;
}

export interface IRTBaseCampaignWithSC extends IRTBaseCampaignParams {
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    keywords?; // add type and remove question mark? check if keywords should be here.
}

interface IRTCampaignMetaData {
    isSubscribe: boolean;
}

export interface IRTCreateCampaign {
    campaign: IRTBaseCampaignWithSC;
    metaData: IRTCampaignMetaData;
}

export interface IRTCurrentCampaignData extends IRTBaseCampaignWithSC {
    id: string;
    asset: string;
    color: string;
    createdTime: Dayjs;
    updatedTime: Dayjs;
    lastMigrationDate: Dayjs | null;
    /** First day with scraping data; `null` or omitted while scrape is pending. */
    firstScrapeTime?: string | null;
    lastScrapeDate: string | null;
    isActive: boolean;
    isShared: boolean;
    createdByUserId: number;
    keywordCount: number;
    isDemo?: boolean;
    isEditable?: boolean;
    competitors: IRankTrackerCompetitor[];
    assetRolesSettings?: Record<"userAssetRoles", AssetUser[]>;
}

export interface IKeywordsCampaignsCampaignRawData {
    id: string;
    name: string;
    site: string;
    competitors: string[];
    isShared: boolean;
    createdTime: Dayjs;
    keywordCount: number;
    /** First day with scraping data; `null` or omitted while scrape is pending. */
    firstScrapeTime?: string | null;
    lastScrapeDate: string | null;
    tags: Record<string, string[]>;
    keywordToTargetUrls: Record<string, string[]>;
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    createdBy: string;
    entities: {
        [entity: string]: string[];
    };
    trackingScope?: ValueOf<typeof WebsitePageSearchTypeNumbers>;
    isEditable?: boolean;
}

export interface IKeywordsCampaigns {
    userCampaigns: IKeywordsCampaignsCampaignRawData[];
    sharedCampaigns: IKeywordsCampaignsCampaignRawData[];
}

export interface IKeywordsCampaignsContext {
    loadingState: ECampaignsLoadingState;
    currentCampaignConfiguration: IRTCurrentCampaignData;
    campaignsConfigurations: IKeywordsCampaignData[];
    updateCampaignsFromServer: VoidFunction;
    locationsAndLanguagesLists: ICampaignLocationAndLanguage;
    durationObject: DatesRange;
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics;
    updateCampaignsConsumptionFromServer: VoidFunction;
    isLoadingLanguagesAndLocations: boolean;
    myUserConsumptionMetrics: IMyUserConsumptionMetrics;
    demoCampaigns: IKeywordsCampaignData[];
    updateLocalCampaignsAfterCreation: (campaignData: IRTCurrentCampaignData) => void;
    updateLocalCampaignsAfterEdit: (campaignData: IKeywordsCampaignData) => void;
    updateLocalCurrentCampaignConfiguration: (campaignData: Partial<IRTCurrentCampaignData>) => void;
    updateLocalCampaignsAfterDelete: (campaignId: string) => void;
    updateCampaignsDataFromServer: () => Promise<void>;
    updateCurrentCampaign: (skipLoading?: boolean) => Promise<unknown>;
}

export interface IKeywordsCampaignsProviderProps {
    children: ReactNode;
}

export interface RTContextState {
    currentCampaignConfiguration: IRTCurrentCampaignData;
    campaignsConfigurations: IKeywordsCampaignData[];
    loadingState: ECampaignsLoadingState;
    isLoadingLanguagesAndLocations: boolean;
    demoCampaigns: IKeywordsCampaignData[];
    durationObject: DatesRange;
    myUserConsumptionMetrics: IMyUserConsumptionMetrics;
    locationsAndLanguagesLists: ICampaignLocationAndLanguage;
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics;
}

export interface ITargetUrlUpdatedDate {
    keyword: string;
    keywordToTargetUrls: string[];
}

export interface IScarpingWithDetails {
    scrapingConfigurations: ICampaignsBaseScrapingConfiguration[];
    tags: IKeywordsCampaignsScrapingKeywords;
    keywordToTargetUrls: IKeywordsCampaignsScrapingKeywords;
}

export enum TabFilter {
    ALL = 0,
    MY = 1,
    EDITABLE = 2,
    SHARED = 3,
    DEMO = 4,
}

export const TabFilterNaming = Object.keys(TabFilter);

export type ISharedRankTracker = IKeywordsCampaignData & { sharedBy?: string; isShared?: boolean };
export type TableData = Array<IKeywordsCampaignData>;
type DropdownIDValues = "edit" | "permissions" | "share" | "subscribe" | "delete";
export type DropdownOnClickProps = { id: DropdownIDValues };
export interface KeywordsCampaignCreateApiResponse {
    competitors: string[];
    entities: Entities;
    tags: Tags;
    keywordToTargetUrls: KeywordToTargetUrls;
    trackingScope: number;
    lastMigrationDate: string;
    id: string;
    name: string;
    site: string;
    createdTime: string;
    updatedTime: string;
    isActive: boolean;
    createdByUserId: number;
    keywordCount: number;
    scrapingConfigurations: ScrapingConfiguration[];
    assetRolesSettings: AssetRolesSettings;
}

interface Entities {
    youTubeChannels: string[];
    googleBusinessProfiles: string[];
    tikTokChannels: string[];
    twitterAccounts: string[];
}

type Tags = Record<string, string[]>;

type KeywordToTargetUrls = Record<string, string[]>;

interface ScrapingConfiguration {
    keywords: string[];
    id: string;
    searchEngine: string;
    geoLocation: string;
    device: string;
    locale: string;
    geoLocationType: string;
    geoLocationCountry: number;
}

interface AssetRolesSettings {
    userAssetRoles: UserAssetRole[];
}

interface UserAssetRole {
    userId: number;
    assetRole: number;
}

/** Campaign fields used for RT navigation defaults and scrape-aware duration bounds. */
export type RtCampaignScrapeContext = Pick<
    IRTCurrentCampaignData,
    "id" | "scrapingConfigurations" | "site" | "createdTime" | "firstScrapeTime" | "lastScrapeDate"
>;
